import React from "react";
import { Avatar, Box, IconButton, Tooltip, Typography } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { User, ViewMode, DayTask } from "./types";
import { fmtDayNum, fmtDayShort, ymd } from "./dateUtils";
import { TaskCard } from "./TaskCard";

interface WorkloadGridProps {
  users: User[];
  viewMode: ViewMode;
  viewDays: Date[];
  leftColWidth: number;
  zoomed: number;
  onTaskClick?: (taskId: string) => void;
  onDateHeaderClick?: (date: Date) => void;
}

export const WorkloadGrid: React.FC<WorkloadGridProps> = ({
  users,
  viewMode,
  viewDays,
  leftColWidth,
  zoomed,
  onTaskClick,
  onDateHeaderClick,
}) => {
  const gridTemplateColumns = `${leftColWidth}px repeat(${viewDays.length}, 1fr)`;

  if (users.length === 0) return null;

  const handleTaskCardClick = (taskId: string) => {
    if (onTaskClick) {
      onTaskClick(taskId);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      {/* ✅ SINGLE transform wrapper for BOTH header AND data rows */}
      <Box
        sx={{
          transform: `scale(${zoomed})`,
          transformOrigin: "0 0",
          width: `${100 / zoomed}%`,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridTemplateColumns,
            borderBottom: "1px solid #E5E7EB",
            position: "sticky",
            top: 0,
            bgcolor: "white",
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: "#F9FAFB",
              borderRight: "1px solid #E5E7EB",
              display: "flex",
              alignItems: "center",
              gap: 0.2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Work Load
            </Typography>
          </Box>
          {viewDays?.map((d, idx) => {
            const isToday = ymd(d) === ymd(today);

            return (
              <Tooltip title="View Summary" key={idx}>
                <Box
                  onClick={() => onDateHeaderClick && onDateHeaderClick(d)}
                  sx={{
                    px: viewMode === "month" ? 0.5 : 1.5,
                    py: 1,
                    bgcolor: isToday ? "#EFF6FF" : "#F9FAFB",
                    borderRight:
                      idx < viewDays.length - 1 ? "1px solid #E5E7EB" : "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: viewMode === "month" ? 80 : "auto",
                    cursor: "pointer",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontSize: viewMode === "month" ? 11 : 14,
                      fontWeight: 600,
                      color: isToday ? "#2563EB" : "inherit",
                    }}
                  >
                    {fmtDayNum(d)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isToday ? "#2563EB" : "#6B7280",
                      fontSize: viewMode === "month" ? 9 : 11,
                      fontWeight: isToday ? 600 : 400,
                    }}
                  >
                    {fmtDayShort(d)}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* Data rows - IN THE SAME transform wrapper */}
        {users?.map((u, rowIdx) => {
          const total = viewDays.reduce((acc, d) => {
            const k = ymd(d);
            const dayTasks = u.tasksByDate[k] || [];
            return acc + dayTasks.reduce((s, t) => s + t.hours, 0);
          }, 0);

          return (
            <Box
              key={u.id}
              sx={{
                display: "grid",
                gridTemplateColumns: gridTemplateColumns,
                borderBottom: "1px solid #E5E7EB",
                minHeight: viewMode === "month" ? 80 : 100,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  px: viewMode === "month" ? 1 : 2,
                  py: 1.5,
                  borderRight: "1px solid #E5E7EB",
                  bgcolor: "#FAFAFA",
                }}
              >
                <IconButton size="small" sx={{ mt: 0.5 }}>
                  {u.enabled ? (
                    <CheckBoxIcon fontSize="small" sx={{ color: "#10B981" }} />
                  ) : (
                    <CheckBoxOutlineBlankIcon fontSize="small" />
                  )}
                </IconButton>
                <Avatar
                  sx={{
                    width: viewMode === "month" ? 32 : 40,
                    height: viewMode === "month" ? 32 : 40,
                    mt: 0.5,
                  }}
                  src={u.avatarUrl}
                >
                  {u.name
                    .split(" ")
                    .map((p) => p[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    noWrap
                    title={u.name}
                    sx={{
                      fontSize: viewMode === "month" ? 13 : 14,
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    {u.name}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mt: 1,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#111827",
                        fontSize: viewMode === "month" ? 10 : 12,
                        fontWeight: 600,
                      }}
                    >
                      {u.weeklyCapacityHours} h
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        alignItems: "center",
                        fontSize: viewMode === "month" ? 9 : 10,
                      }}
                    >
                      {["M", "T", "W", "T", "F", "S", "S"].map((day, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            width: viewMode === "month" ? 14 : 16,
                            height: viewMode === "month" ? 14 : 16,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: viewMode === "month" ? 8 : 9,
                            color: "#6B7280",
                            fontWeight: 500,
                          }}
                        >
                          {day}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
                {viewMode === "sprint" && (
                  <IconButton size="small">
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {viewDays.map((d, colIdx) => {
                const key = ymd(d);
                const tasks = (u.tasksByDate[key] || []) as DayTask[];
                const isToday = ymd(d) === ymd(today);

                return (
                  <Box
                    key={`${rowIdx}-${colIdx}`}
                    sx={{
                      borderRight:
                        colIdx < viewDays.length - 1
                          ? "1px solid #E5E7EB"
                          : "none",
                      px: viewMode === "month" ? 0.5 : 1,
                      py: viewMode === "month" ? 0.5 : 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: viewMode === "month" ? 0.5 : 0.5,
                      position: "relative",
                      minWidth: viewMode === "month" ? 80 : "auto",
                      bgcolor: isToday ? "#F0F9FF" : "white",
                      overflow: "hidden",
                    }}
                  >
                    {tasks.length === 0 ? (
                      <Box
                        sx={{
                          flex: 1,
                          borderRadius: 1,
                          border: "1px dashed #E5E7EB",
                          bgcolor: isToday ? "#F0F9FF" : "#FCFCFD",
                          minHeight: viewMode === "month" ? 40 : 60,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#D1D5DB",
                            fontSize: viewMode === "month" ? 9 : 10,
                          }}
                        >
                          {viewMode === "sprint" ? "" : ""}
                        </Typography>
                      </Box>
                    ) : (
                      tasks.map((t, i) => (
                        <Box
                          key={i}
                          onClick={() => handleTaskCardClick(t.id)}
                          sx={{
                            cursor: "pointer",
                            transition: "transform 0.15s ease",
                            "&:hover": {
                              transform: "scale(1.01)",
                            },
                            ...(viewMode === "month" && {
                              "& .MuiTypography-root": {
                                fontSize: "0.7rem",
                              },
                            }),
                          }}
                        >
                          <TaskCard task={t} />
                        </Box>
                      ))
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
